import { Feature, Point } from 'geojson'
import { MapboxMap } from 'react-map-gl'
import { bbox } from 'turf'

const validateCoordinates = (minLng: number, minLat: number, maxLng: number, maxLat: number): boolean => {
  if (!isFinite(minLng) || !isFinite(minLat) || !isFinite(maxLng) || !isFinite(maxLat)) {
    console.warn('[mapUtils] Invalid coordinates detected:', { minLng, minLat, maxLng, maxLat })
    return false
  }

  if (minLat < -90 || minLat > 90 || maxLat < -90 || maxLat > 90) {
    console.warn('[mapUtils] Latitude out of bounds:', { minLat, maxLat })
    return false
  }

  if (minLng < -180 || minLng > 180 || maxLng < -180 || maxLng > 180) {
    console.warn('[mapUtils] Longitude out of bounds:', { minLng, maxLng })
    return false
  }

  return true
}

export type GeoMapFitOptions = {
  /** Left sidebar width in px — shifts visible map center right (RobotView panel ≈ 582). */
  leftPanelInsetPx?: number
  maxZoom?: number
  duration?: number
}

export function geoPointFeature(lng: number, lat: number): Feature {
  return {
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [lng, lat] },
    properties: {},
  }
}

export function geoPointsToFeatures(coordinates: Array<[number, number]>): Feature[] {
  return coordinates.map(([lng, lat]) => geoPointFeature(lng, lat))
}

/** WGS84 [lng, lat] from live telemetry or last known robot position. */
export function resolveRobotGeoCoordinate(
  livePosition?: [number, number] | null,
  lastPosition?: Point | null
): [number, number] | null {
  if (livePosition?.length === 2) {
    const [lng, lat] = livePosition
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat]
    }
  }
  const last = lastPosition?.coordinates
  if (last && last.length >= 2) {
    const lng = last[0]
    const lat = last[1]
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      return [lng, lat]
    }
  }
  return null
}

/** Fit geographic map to one or more WGS84 points, accounting for a left UI panel. */
export function focusMapOnGeoPoints(
  map: MapboxMap | undefined,
  coordinates: Array<[number, number]>,
  options: GeoMapFitOptions = {}
) {
  if (!coordinates.length) {
    return
  }
  focusMapOnGeoFeatures(map, geoPointsToFeatures(coordinates), options)
}

export function focusMapOnGeoPoint(
  map: MapboxMap | undefined,
  lng: number,
  lat: number,
  options: GeoMapFitOptions = {}
) {
  focusMapOnGeoFeatures(map, [geoPointFeature(lng, lat)], options)
}

export function focusMapOnGeoFeatures(map: MapboxMap | undefined, features: Feature[], options: GeoMapFitOptions = {}) {
  const { leftPanelInsetPx = 0, maxZoom = 21, duration = 350 } = options
  zoomMapTo(map, features, leftPanelInsetPx, maxZoom, duration)
}

type MapPadding = { top: number; bottom: number; right: number; left: number }

/** Clamp panel insets so Mapbox fitBounds/easeTo never gets padding larger than the canvas. */
function mapCameraPadding(mapWidth: number, mapHeight: number, offset: number): MapPadding | null {
  if (mapWidth <= 0 || mapHeight <= 0) {
    return null
  }
  // Leave at least ~40px usable in each axis; otherwise camera math throws
  // "Map cannot fit within canvas with the given bounds, padding, and/or offset".
  const maxHorizontal = Math.max(0, mapWidth - 40)
  const maxVertical = Math.max(0, mapHeight - 40)
  const rightPadding = Math.min(100, maxHorizontal / 2)
  const leftBuffer =
    offset > 0 ? Math.min(offset + 40, mapWidth * 0.45, maxHorizontal - rightPadding) : rightPadding
  const top = Math.min(140, maxVertical / 2)
  const bottom = Math.min(100, maxVertical - top)
  if (leftBuffer + rightPadding >= mapWidth || top + bottom >= mapHeight) {
    return null
  }
  return { top, bottom, right: rightPadding, left: Math.max(0, leftBuffer) }
}

function fitOrCenterFeatures(
  map: any,
  features: Array<Feature>,
  padding: MapPadding,
  duration: number,
  maxZoom: number
) {
  const [minLng, minLat, maxLng, maxLat] = bbox({
    type: 'FeatureCollection',
    features: features,
  })

  if (!validateCoordinates(minLng, minLat, maxLng, maxLat)) {
    return
  }

  // Degenerate (single-point) bounds + large padding often fail fitBounds; easeTo is stable.
  const isPoint = Math.abs(maxLng - minLng) < 1e-12 && Math.abs(maxLat - minLat) < 1e-12
  if (isPoint) {
    map.easeTo({
      center: [minLng, minLat],
      zoom: Math.min(maxZoom, typeof map.getMaxZoom === 'function' ? map.getMaxZoom() : maxZoom),
      duration,
      padding,
    })
    return
  }

  map.fitBounds(
    [
      [minLng, minLat],
      [maxLng, maxLat],
    ],
    {
      padding,
      duration,
      maxZoom,
    }
  )
}

export const zoomMapTo = (
  map: any | undefined,
  features: Array<Feature>,
  offset: number = 0,
  pointZoom: number = 21,
  duration: number = 350
) => {
  try {
    if (!map || !features.length) {
      return
    }

    if (typeof map.stop === 'function') {
      map.stop()
    }

    const container = map.getContainer()
    const mapWidth = container.clientWidth
    const mapHeight = container.clientHeight
    const padding = mapCameraPadding(mapWidth, mapHeight, offset)
    if (!padding) {
      return
    }

    fitOrCenterFeatures(map, features, padding, duration, pointZoom)
  } catch (error) {
    console.error('[mapUtils] Error in zoomMapTo:', error)
  }
}

export const centerMapTo = (map: any | undefined, features: Array<Feature>, offset: number = 0) => {
  try {
    if (!map || !features.length) {
      return
    }

    const container = map.getContainer()
    const mapWidth = container.clientWidth
    const mapHeight = container.clientHeight
    const padding = mapCameraPadding(mapWidth, mapHeight, offset)
    if (!padding) {
      return
    }

    const maxZoom = typeof map.getZoom === 'function' ? map.getZoom() : 21
    fitOrCenterFeatures(map, features, padding, 300, maxZoom)
  } catch (error) {
    console.error('[mapUtils] Error in centerMapTo:', error)
  }
}

/**
 * Mapbox layer id to insert station / service-point pins *below* robot markers.
 * Prefer the bottom of the robot stack (cluster circle when fleet map is active).
 */
export function getBeforeIdUnderRobotMarkers(
  map: { getLayer?: (id: string) => unknown } | null | undefined
): string | undefined {
  if (!map?.getLayer) {
    return undefined
  }
  for (const id of ['robot-cluster-circle', 'robot-image-style', 'robot-editable-image-style']) {
    if (map.getLayer(id)) {
      return id
    }
  }
  return undefined
}
